import { useState, useMemo } from "react";
import { Modal } from "../../components/Modal";
import { Drawer } from "../../components/Drawer";
import { CreditCard } from "../../components/shared/CreditCard";
import { Typography } from "../../components/shared/Typography";
import { useCardData } from "../../hooks/useCardData";
import { useCreditData } from "../../hooks/useCreditData";
import {
  useCardDetails,
  CardDetailsCallbacks,
} from "../../hooks/useCardDetails";
import ChevronRightIcon from "../../components/icons/ChevronRightIcon";
import { getCreditProvider } from "../../config/creditRegistry";
import {
  InfoIcon,
  ContentWrapper,
  WalletTag,
  FreezeCardTransactions,
  CustomButton,
  CustomInput,
} from "./components";
import PencilIcon from "../../components/icons/PencilIcon";
import { Cross1Icon } from "@radix-ui/react-icons";
import { useIsMobile } from "./useIsMobile";

type ViewType = "main" | "changePin" | "transactionLimit";

const MAX_PER_TRANSACTION_LIMIT = "10,000";
interface ManageCardModalProps {
  title: string;
  onClose: () => void;
  callbacks: CardDetailsCallbacks;
  address: string;
}

// Move CardContent outside to prevent re-creation on every render
const CardContent = ({
  userCard,
  cardNumber,
  cardLimit,
  showCardDetails,
  expiry,
  decryptedCVC,
  isLoading,
  handleShowCardDetails,
  isTransitioning,
  renderView,
  isMobile,
}: {
  userCard: any;
  cardNumber: string;
  cardLimit: number;
  showCardDetails: boolean;
  expiry: string;
  decryptedCVC: string | null;
  isLoading: boolean;
  handleShowCardDetails?: () => void;
  isTransitioning: boolean;
  renderView: React.ReactNode;
  isMobile: boolean;
}) => (
  <div
    style={{
      display: "flex",
      flexDirection: "column",
    }}
    className={isMobile ? "text-white" : ""}
  >
    {/* Card Display */}
    <div className="px-[12px] py-[12px] flex justify-center">
      <CreditCard
        solverType={userCard?.solverType}
        number={cardNumber}
        limit={cardLimit}
        showPin={showCardDetails}
        expiry={showCardDetails ? expiry : `**/****`}
        cvv={decryptedCVC || "***"}
        isLoading={isLoading}
        onToggleViewPin={
          // if card is not active, do not allow toggling pin view
          userCard?.status === "active" ? handleShowCardDetails : undefined
        }
      />
    </div>

    {/* Card Status */}
    <div className="px-[12px] pb-[12px] flex items-center justify-between">
      <Typography
        className="text-[#A8ADB5] text-[14px] sm:text-base"
        style={{ color: "#A8ADB5" }}
      >
        Card Status
      </Typography>
      <div className="bg-[rgba(102,219,148,0.10)] text-[#66db94] rounded-[4px] px-3 py-1 text-[12px]">
        {userCard?.status === "active"
          ? "Active"
          : userCard?.status === "inactive"
          ? "Inactive"
          : userCard?.status === "locked"
          ? "Locked"
          : "Pending"}
      </div>
    </div>

    <div
      className={`rounded-[4px] flex gap-[2px] flex-col overflow-hidden transition-opacity duration-300 ease-in-out ${
        isTransitioning ? "opacity-0" : "opacity-100"
      }`}
    >
      {renderView}
    </div>
  </div>
);

export function ManageCardModal({
  title,
  onClose,
  callbacks,
  address,
}: ManageCardModalProps) {
  const { creditInfo } = useCreditData();
  const { userCard } = useCardData();
  const [currentView, setCurrentView] = useState<ViewType>("main");
  const [newPin, setNewPin] = useState("");
  const [confirmPin, setConfirmPin] = useState("");
  const [isTransitioning, setIsTransitioning] = useState(false);
  const [isEditingLimit, setIsEditingLimit] = useState(false);
  const isMobile = useIsMobile();

  const {
    pin,
    limit,
    inputLimit,
    expiry,
    showCardDetails,
    decryptedCardNumber,
    decryptedCVC,
    isLoading,
    handleUpdateLimit,
    handleShowCardDetails,
    handleFreezeCard,
    handleUpdatePin,
    handlePinChange,
    handleLimitChange,
  } = useCardDetails({ userCard, callbacks, address });

  const cardNumber =
    showCardDetails && decryptedCardNumber
      ? decryptedCardNumber.match(/.{1,4}/g)?.join(" ") || ""
      : userCard
      ? `**** **** **** ${userCard.last4}`
      : "";

  const cardLimit = useMemo(() => {
    if (!userCard) return 0;

    const provider = getCreditProvider(userCard.solverType);

    return provider?.storeKey
      ? creditInfo[provider.storeKey]?.data?.availableLimit || 0
      : 0;
  }, [userCard, creditInfo]);

  // Pin validation logic
  const isPinValid = newPin.length > 0 && newPin === confirmPin;

  // Limit validation logic
  const isLimitValid = inputLimit.length > 0;
  const isLimitChanged = inputLimit !== limit;

  const handleSavePin = () => {
    if (isPinValid && !isLoading) {
      handleUpdatePin(newPin);
      // Reset pin inputs after successful save
      setNewPin("");
      setConfirmPin("");
    }
  };

  const handleSaveLimit = () => {
    if (isLimitValid && isLimitChanged && !isLoading) {
      handleUpdateLimit(inputLimit);
    }
  };

  // Handle view transition with fade effect
  const handleViewChange = (newView: ViewType) => {
    setIsTransitioning(true);
    setTimeout(() => {
      setCurrentView(newView);
      setIsTransitioning(false);
    }, 150);
  };

  const renderView = useMemo(() => {
    switch (currentView) {
      case "main":
        return (
          <>
            <ContentWrapper
              isHoverable
              onClick={() => handleViewChange("changePin")}
            >
              <div className="flex justify-between w-full">
                <Typography className="text-[#fff] text-sm sm:text-base">
                  Change Pin
                </Typography>
                <ChevronRightIcon />
              </div>
            </ContentWrapper>
            <ContentWrapper
              isHoverable
              onClick={() => handleViewChange("transactionLimit")}
            >
              <div className="flex justify-between w-full">
                <Typography className="text-[#fff] text-sm sm:text-base">
                  Transaction Limit{" "}
                  <span
                    className="text-[#A8ADB5] text-[14px]"
                    style={{ color: "#A8ADB5" }}
                  >
                    (${limit} currently)
                  </span>
                </Typography>
                <ChevronRightIcon />
              </div>
            </ContentWrapper>
            <ContentWrapper>
              <div className="flex justify-between w-full">
                <Typography className="text-[#fff] text-sm sm:text-base">
                  Connected Wallet
                </Typography>
                <WalletTag address={address} />
              </div>
            </ContentWrapper>
            <ContentWrapper>
              <div className="flex justify-between w-full">
                <Typography className="text-[#fff] text-sm sm:text-base">
                  Freeze Card Transactions
                </Typography>
                <FreezeCardTransactions
                  isLoading={isLoading}
                  handleFreezeCard={handleFreezeCard}
                  userCard={userCard}
                />
              </div>
            </ContentWrapper>
            <ContentWrapper>
              <div className="flex items-start gap-2 text-xs text-[#888]">
                <InfoIcon className="w-4 h-4 flex-shrink-0 mt-1" />
                <Typography className="text-[#CBCED1] text-[14px] font-medium leading-[18px] sm:leading-[20px]">
                  BrahmaFi card is not affiliated with swype.fun, AAVE, Euler,
                  HypurrFi or other Defi and issued subject to separate terms
                  provided by Issuer.
                </Typography>
              </div>
            </ContentWrapper>
          </>
        );

      case "changePin":
        return (
          <>
            <ContentWrapper>
              <div className="flex flex-col gap-[10px]">
                <Typography className="text-[rgb(168,173,181)]">
                  Change Pin
                </Typography>

                <CustomInput
                  type="password"
                  placeholder="Enter New Pin"
                  value={newPin}
                  onChange={(e) => setNewPin(e.target.value)}
                />
                <CustomInput
                  type="password"
                  placeholder="Confirm New Pin"
                  value={confirmPin}
                  onChange={(e) => setConfirmPin(e.target.value)}
                />
                {newPin.length > 0 && confirmPin.length > 0 && !isPinValid && (
                  <Typography className="text-[#F45562] text-sm">
                    Pins do not match
                  </Typography>
                )}
              </div>
            </ContentWrapper>

            <ContentWrapper>
              <div className="flex items-start gap-2 text-xs text-[#888]">
                <InfoIcon className="w-4 h-4 flex-shrink-0 mt-1" />
                <Typography className="text-[#CBCED1] text-[14px] font-medium leading-[18px] sm:leading-[20px]">
                  Changing your card PIN will deactivate the old one for all
                  transactions. Make sure to use your new PIN going forward.
                </Typography>
              </div>
            </ContentWrapper>

            <ContentWrapper>
              <div className="flex flex-col gap-[12px]">
                <CustomButton
                  disabled={!isPinValid || isLoading}
                  onClick={handleSavePin}
                >
                  <Typography>Save Pin</Typography>
                </CustomButton>
                <CustomButton
                  variant="secondary"
                  onClick={() => handleViewChange("main")}
                >
                  <div className="flex items-center gap-2">
                    <span className="transform rotate-180">
                      <ChevronRightIcon />
                    </span>
                    <Typography>Back</Typography>
                  </div>
                </CustomButton>
              </div>
            </ContentWrapper>
          </>
        );

      case "transactionLimit":
        return (
          <>
            <ContentWrapper>
              <div className="flex flex-col gap-2">
                <div className="flex justify-between w-full">
                  <Typography
                    className="text-white text-base font-medium leading-6"
                    style={{ fontFamily: '"Neue Montreal"' }}
                  >
                    Daily Spending Limit
                  </Typography>{" "}
                  <div className="flex items-center gap-2">
                    <Typography className="text-[#fff] text-sm sm:text-base">
                      ${limit}
                    </Typography>
                    <div
                      className="rounded-[2px] border border-[#494C56] bg-[#292930] flex w-6 h-6 p-1 justify-center items-center gap-1 cursor-pointer hover:bg-[#3A3B42] hover:border-[#5A5D66] transition-colors duration-200"
                      onClick={() => setIsEditingLimit((prev) => !prev)}
                    >
                      {isEditingLimit ? (
                        <Cross1Icon color="white" />
                      ) : (
                        <PencilIcon color="white" />
                      )}
                    </div>
                  </div>
                </div>
                {isEditingLimit && (
                  <CustomInput
                    type="text"
                    placeholder="Edit Daily Spending Limit"
                    value={inputLimit}
                    onChange={handleLimitChange}
                  />
                )}
              </div>
            </ContentWrapper>{" "}
            <ContentWrapper>
              <div className="flex flex-col gap-2">
                <div className="flex justify-between w-full">
                  <Typography
                    className="text-white text-base font-medium leading-6"
                    style={{ fontFamily: '"Neue Montreal"' }}
                  >
                    Max Per Transaction{" "}
                    <span className="text-[#6D7178] text-[16px]">(Fixed)</span>
                  </Typography>{" "}
                  <div className="flex items-center gap-2">
                    <Typography className="text-[#fff] text-sm sm:text-base">
                      ${MAX_PER_TRANSACTION_LIMIT}
                    </Typography>
                  </div>
                </div>
              </div>
            </ContentWrapper>
            <ContentWrapper>
              <div className="flex items-start gap-2 text-xs text-[#888]">
                <InfoIcon className="w-4 h-4 flex-shrink-0 mt-1" />
                <Typography className="text-[#CBCED1] text-[14px] font-medium leading-[18px] sm:leading-[20px]">
                  Your daily transaction limit is the maximum you can spend in a
                  single day across multiple purchases. The per-transaction
                  limit is fixed at ${MAX_PER_TRANSACTION_LIMIT}.
                </Typography>
              </div>
            </ContentWrapper>
            <ContentWrapper>
              <div className="flex flex-col gap-[12px]">
                <CustomButton
                  disabled={!isLimitValid || !isLimitChanged || isLoading}
                  onClick={handleSaveLimit}
                >
                  <Typography>Update Daily Limit</Typography>
                </CustomButton>
                <CustomButton
                  variant="secondary"
                  onClick={() => handleViewChange("main")}
                >
                  <div className="flex items-center gap-2">
                    <span className="transform rotate-180">
                      <ChevronRightIcon />
                    </span>
                    <Typography>Back</Typography>
                  </div>
                </CustomButton>
              </div>
            </ContentWrapper>
          </>
        );

      default:
        return null;
    }
  }, [
    currentView,
    limit,
    inputLimit,
    address,
    isLoading,
    userCard,
    handleFreezeCard,
    newPin,
    confirmPin,
    isPinValid,
    isLimitValid,
    isLimitChanged,
    handleSavePin,
    handleSaveLimit,
    handleLimitChange,
  ]);

  // Render Modal for desktop, Drawer for mobile
  if (isMobile) {
    return (
      <Drawer
        isOpen={true}
        onClose={onClose}
        className="px-[4px] py-[4px] text-white bg-black"
      >
        <CardContent
          userCard={userCard}
          cardNumber={cardNumber}
          cardLimit={cardLimit}
          showCardDetails={showCardDetails}
          expiry={expiry}
          decryptedCVC={decryptedCVC}
          isLoading={isLoading}
          handleShowCardDetails={
            userCard?.status === "active" ? handleShowCardDetails : undefined
          }
          isTransitioning={isTransitioning}
          renderView={renderView}
          isMobile={isMobile}
        />
      </Drawer>
    );
  }

  return (
    <Modal title={title} onClose={onClose} padding="px-[4px] py-[4px] ">
      <CardContent
        userCard={userCard}
        cardNumber={cardNumber}
        cardLimit={cardLimit}
        showCardDetails={showCardDetails}
        expiry={expiry}
        decryptedCVC={decryptedCVC}
        isLoading={isLoading}
        handleShowCardDetails={
          userCard?.status === "active" ? handleShowCardDetails : undefined
        }
        isTransitioning={isTransitioning}
        renderView={renderView}
        isMobile={isMobile}
      />
    </Modal>
  );
}
